import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../services/api';

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;
const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

const COLUMNS = [
  '참여연도',
  '순번',
  '입사일',
  '퇴사일',
  '읍면동',
  '성명',
  '주민번호',
  '은행',
  '계좌번호',
  '실제근무시간',
  '연차사용시간',
  '주휴시간',
  '연차미사용시간',
  '시급',
  '월급여',
  '주휴수당',
  '연차수당',
  '급여총액',
  '건강보험(본인)',
  '건강보험(기관)',
  '요양보험(본인)',
  '요양보험(기관)',
  '고용보험(본인)',
  '고용보험(기관)',
  '산재보험(기관)',
  '기관부담금',
  '본인부담금',
  '차감지급액',
  '인건비차감분',
  '상태',
];

const filterGroup = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '16px',
};

const labelStyle = { fontWeight: 600 };
const inputStyle = {
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid #d0d7de',
};

const buttonStyle = {
  padding: '10px 16px',
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const secondaryButton = {
  ...buttonStyle,
  background: '#475569',
};

const thStyle = {
  background: '#f2f4f6',
  borderBottom: '1px solid #d0d7de',
  padding: '8px 6px',
  textAlign: 'center',
  verticalAlign: 'middle',
  fontWeight: 600,
  fontSize: '0.72rem',
  whiteSpace: 'nowrap',
  minWidth: '72px',
};

const tdStyle = {
  borderBottom: '1px solid #eef2f6',
  padding: '8px 6px',
  textAlign: 'right',
  verticalAlign: 'middle',
  fontSize: '0.75rem',
};

/** 입사일·퇴사일·읍면동·성명·주민번호·은행·계좌번호 — 값 가운데 정렬 */
const CENTER_VALUE_COLUMNS = new Set([
  '참여연도',
  '입사일',
  '퇴사일',
  '읍면동',
  '성명',
  '주민번호',
  '은행',
  '계좌번호',
  '상태',
]);

/** 조회월 이전 퇴사자 행에서 엑셀과 동일하게 강조할 열 */
const PRIOR_RESIGNED_HIGHLIGHT_COLUMNS = new Set([
  '순번',
  '입사일',
  '퇴사일',
  '읍면동',
  '성명',
  '주민번호',
  '은행',
  '계좌번호',
]);

function queryMonthStart(year, month) {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

/** 조회월 1일 이전 퇴사(전월포함 이전퇴사자) 여부 */
function isPriorResignedRow(row, year, month) {
  const endedDate = String(row?.퇴사일 || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endedDate)) return false;
  return endedDate < queryMonthStart(year, month);
}

/** 목록 표시만: 참여연도 오름·내림차순(엑셀은 조회 시 서버 정렬 순서 유지) */
function sortRowsByParticipantYear(rows, order) {
  if (order === 'none') return rows;
  const nYear = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : null;
  };
  return [...rows].sort((a, b) => {
    const pyA = nYear(a.참여연도);
    const pyB = nYear(b.참여연도);
    const na = a.성명 || '';
    const nb = b.성명 || '';
    let cmp = 0;
    if (order === 'asc') {
      cmp = (pyA !== null ? pyA : 99999) - (pyB !== null ? pyB : 99999);
    } else {
      cmp = (pyB !== null ? pyB : -99999) - (pyA !== null ? pyA : -99999);
    }
    if (cmp !== 0) return cmp;
    return na.localeCompare(nb, 'ko');
  });
}

/** 목록 표시만: 정상(0) → 불일치(1) 또는 역순, 동률 시 성명 */
function sortRowsByDataError(rows, order) {
  if (order === 'none') return rows;
  const rank = (row) => ((row.mismatchedColumns || []).length > 0 ? 1 : 0);
  return [...rows].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    let cmp = order === 'asc' ? ra - rb : rb - ra;
    if (cmp !== 0) return cmp;
    const na = a.성명 || '';
    const nb = b.성명 || '';
    return na.localeCompare(nb, 'ko');
  });
}

function cycleColumnSort(prev) {
  return prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none';
}

const formatCell = (v) => {
  if (v === null || v === undefined || v === '') return '';
  let n = null;
  if (typeof v === 'number' && Number.isFinite(v)) n = v;
  else if (typeof v === 'string' && v.trim() !== '' && /^-?\d+(\.\d+)?$/.test(v.trim())) {
    const parsed = Number(v.trim());
    if (Number.isFinite(parsed)) n = parsed;
  }
  if (n !== null && Number.isFinite(n)) {
    return Number.isInteger(n)
      ? n.toLocaleString('ko-KR')
      : n.toLocaleString('ko-KR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
  }
  return String(v);
};

function SocialInsuranceExportPage() {
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [participantYearScope, setParticipantYearScope] = useState('all');
  const [statusScope, setStatusScope] = useState('all');
  const [nameSearch, setNameSearch] = useState('');
  const [participantYearColumnSort, setParticipantYearColumnSort] = useState('none');
  const [dataErrorColumnSort, setDataErrorColumnSort] = useState('none');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [mismatchModal, setMismatchModal] = useState({ open: false, count: 0 });

  const countMismatchRows = useCallback((list) => {
    if (!Array.isArray(list)) return 0;
    return list.filter((r) => (r.mismatchedColumns || []).length > 0).length;
  }, []);

  const syncMismatchModalWithRows = useCallback(
    (list) => {
      const n = countMismatchRows(list);
      setMismatchModal(n > 0 ? { open: true, count: n } : { open: false, count: 0 });
    },
    [countMismatchRows]
  );

  const displayRows = useMemo(() => {
    if (participantYearColumnSort !== 'none') {
      return sortRowsByParticipantYear(rows, participantYearColumnSort);
    }
    if (dataErrorColumnSort !== 'none') {
      return sortRowsByDataError(rows, dataErrorColumnSort);
    }
    return rows;
  }, [rows, participantYearColumnSort, dataErrorColumnSort]);

  const fetchProjects = async (y = year) => {
    try {
      const res = await api.get('/projects', { params: { year: y } });
      setProjects(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchReport = async (overrides = {}) => {
    const effectiveProjectId = overrides.projectId ?? projectId;
    setLoading(true);
    setMessage(null);
    try {
      const res = await api.get('/insurance/export-report', {
        params: {
          year,
          month,
          participantYearScope,
          ...(statusScope !== 'all' ? { statusScope } : {}),
          ...(nameSearch.trim() ? { name: nameSearch.trim() } : {}),
          ...(effectiveProjectId ? { projectId: effectiveProjectId } : {}),
          format: 'json',
        },
      });
      const list = res.data?.rows || [];
      setRows(list);
      setParticipantYearColumnSort('none');
      setDataErrorColumnSort('none');
      syncMismatchModalWithRows(list);
    } catch (e) {
      setRows([]);
      setMismatchModal({ open: false, count: 0 });
      setMessage({
        type: 'error',
        text: e.response?.data?.message || '조회에 실패했습니다.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(year);
  }, [year]);

  const handleExcel = async () => {
    setExcelLoading(true);
    setMessage(null);
    try {
      const res = await api.get('/insurance/export-report', {
        params: {
          year,
          month,
          participantYearScope,
          ...(statusScope !== 'all' ? { statusScope } : {}),
          ...(nameSearch.trim() ? { name: nameSearch.trim() } : {}),
          ...(projectId ? { projectId } : {}),
          format: 'xlsx',
        },
        responseType: 'blob',
      });
      const dispo = res.headers['content-disposition'];
      let filename = `social_insurance_${year}_${String(month).padStart(2, '0')}.xlsx`;
      if (dispo && dispo.includes('filename=')) {
        const m = dispo.match(/filename="?([^";]+)"?/);
        if (m) filename = m[1];
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let text = '엑셀 다운로드에 실패했습니다.';
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          const j = JSON.parse(t);
          if (j.message) text = j.message;
        } catch {
          /* ignore */
        }
      } else if (e.response?.data?.message) {
        text = e.response.data.message;
      }
      setMessage({ type: 'error', text });
    } finally {
      setExcelLoading(false);
    }
  };

  const pageRootStyle = {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    width: '100%',
  };

  return (
    <div style={pageRootStyle}>
      <div style={{ flexShrink: 0 }}>
        <h2 style={{ marginBottom: '6px' }}>급여자료 출력</h2>
        <p style={{ color: '#64748b', margin: '0 0 16px', fontSize: '0.9rem' }}>
          선택한 연·월·사업단 기준으로 급여·사회보험 입력·근무·연차를 합쳐 조회합니다. 급여 또는 보험 중 하나라도
          금액이 있으면 포함되며, 모두 0인 경우는 제외됩니다. 값이 없으면 공란으로 표시됩니다.
          <br />
          참여연도는 참여자 등록의 연도이며, 아래「참여자 구분」의 당해·전년은{' '}
          <strong>위에서 선택한 연도</strong>를 기준으로 합니다. 조회·엑셀 모두 동일하게 적용됩니다.
          <br />
          <span style={{ color: '#b91c1c' }}>
            빨간 글씨: 연차수당·연차미사용시간은 정산 금액(연차미사용×연차수당 단가)과 급여 연차수당 불일치,
            건강·요양·고용 본인부담은 급여 공제액과 불일치(1원 단위), 차감지급액은 (급여총액−본인부담금)와 참여자
            급여 등록의 차감지급액(net) 불일치입니다.
          </span>
        </p>
      </div>

      <div style={{ ...filterGroup, flexShrink: 0 }}>
        <label style={labelStyle}>
          연도
          <input
            type="number"
            value={year}
            min={2000}
            style={{ ...inputStyle, marginLeft: '6px', width: '110px' }}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </label>
        <label style={labelStyle}>
          월
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            style={{ ...inputStyle, marginLeft: '6px' }}
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>
                {m}월
              </option>
            ))}
          </select>
        </label>
        <label style={labelStyle}>
          사업단
          <select
            value={projectId}
            onChange={(e) => {
              const nextProjectId = e.target.value;
              setProjectId(nextProjectId);
              fetchReport({ projectId: nextProjectId });
            }}
            style={{ ...inputStyle, marginLeft: '6px', minWidth: '180px' }}
          >
            <option value="">전체</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.projectName}
              </option>
            ))}
          </select>
        </label>
        <label style={labelStyle}>
          참여자 구분
          <select
            value={participantYearScope}
            onChange={(e) => setParticipantYearScope(e.target.value)}
            style={{ ...inputStyle, marginLeft: '6px', minWidth: '160px' }}
          >
            <option value="all">통합</option>
            <option value="current">당해연도 참여자</option>
            <option value="previous">전년도 참여자</option>
          </select>
        </label>
        <label style={labelStyle}>
          상태
          <select
            value={statusScope}
            onChange={(e) => setStatusScope(e.target.value)}
            style={{ ...inputStyle, marginLeft: '6px', minWidth: '120px' }}
          >
            <option value="all">전체</option>
            <option value="match">일치</option>
            <option value="mismatch">불일치</option>
          </select>
        </label>
        <label style={labelStyle}>
          성명 검색
          <input
            type="text"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            placeholder="부분 일치"
            style={{ ...inputStyle, marginLeft: '6px', minWidth: '160px' }}
            autoComplete="off"
          />
        </label>
        <button type="button" style={secondaryButton} onClick={fetchReport} disabled={loading}>
          {loading ? '조회 중…' : '조회'}
        </button>
        <button
          type="button"
          style={{ ...buttonStyle, background: '#0f766e' }}
          onClick={handleExcel}
          disabled={loading || excelLoading}
        >
          {excelLoading ? '엑셀 생성 중…' : '엑셀 다운로드'}
        </button>
      </div>

      {message && (
        <div
          style={{
            flexShrink: 0,
            marginBottom: '12px',
            padding: '12px 14px',
            borderRadius: '8px',
            background: message.type === 'error' ? '#fef2f2' : '#eff6ff',
            color: message.type === 'error' ? '#b91c1c' : '#1d4ed8',
            border: `1px solid ${message.type === 'error' ? '#fecaca' : '#93c5fd'}`,
          }}
        >
          {message.text}
        </div>
      )}

      {mismatchModal.open && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mismatch-dialog-title"
            style={{
              background: '#fff',
              borderRadius: '12px',
              maxWidth: '440px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
            }}
          >
            <div
              id="mismatch-dialog-title"
              style={{
                padding: '18px 20px 12px',
                fontSize: '1.05rem',
                fontWeight: 700,
                color: '#0f172a',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              급여·보험 금액 불일치 안내
            </div>
            <div style={{ padding: '16px 20px 20px', fontSize: '0.9rem', lineHeight: 1.65, color: '#334155' }}>
              <p style={{ margin: '0 0 12px', fontSize: '1rem' }}>
                불일치 건수:{' '}
                <strong style={{ color: '#b91c1c', fontSize: '1.1rem' }}>{mismatchModal.count}건</strong>
                <span style={{ display: 'block', marginTop: '6px', fontSize: '0.82rem', color: '#64748b' }}>
                  (명단 중 한 명이라도 불일치 열이 있으면 1건으로 집계합니다.)
                </span>
              </p>
              <p style={{ margin: '0 0 14px' }}>
                급여 등록과 근무·연차·사회보험 입력을 맞춰 계산한 값이 서로 다르게 나온 경우입니다.
              </p>
              <p style={{ margin: '0 0 14px' }}>
                표에서 <strong style={{ color: '#b91c1c' }}>붉게 표시된 칸</strong>을 확인해 주세요. 엑셀로 받으실 때도
                같은 항목이 구분되어 나갑니다.
              </p>
              <p style={{ margin: 0 }}>
                반드시 해당 급여·보험·근무·연차 정보를 수정·저장하여 불일치를 없애 주세요. 그대로 두면 실제 지급액·
                신고·정산과 어긋날 수 있습니다.
              </p>
            </div>
            <div
              style={{
                padding: '12px 20px 18px',
                display: 'flex',
                justifyContent: 'flex-end',
                background: '#f8fafc',
                borderTop: '1px solid #f1f5f9',
              }}
            >
              <button
                type="button"
                style={{
                  ...buttonStyle,
                  minWidth: '96px',
                }}
                onClick={() => setMismatchModal({ open: false, count: 0 })}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 남은 뷰포트 높이만 사용 → 메인(.main-content) 이중 세로 스크롤 제거, 가로 스크롤은 이 박스 하단에 고정 */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          marginTop: '8px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflowX: 'auto',
            overflowY: 'hidden',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <div
            style={{
              height: '100%',
              overflowY: 'auto',
            }}
          >
            <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
            <thead>
              <tr>
                {COLUMNS.map((col) => {
                  if (col === '참여연도') {
                    return (
                      <th
                        key={col}
                        scope="col"
                        role="columnheader"
                        aria-sort={
                          participantYearColumnSort === 'asc'
                            ? 'ascending'
                            : participantYearColumnSort === 'desc'
                              ? 'descending'
                              : 'none'
                        }
                        title="클릭: 연도 오름차순 → 내림차순 → 기본 순서"
                        onClick={() => {
                          setDataErrorColumnSort('none');
                          setParticipantYearColumnSort((prev) => cycleColumnSort(prev));
                        }}
                        style={{
                          ...thStyle,
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        {col}
                        <span style={{ marginLeft: '4px', fontSize: '0.65rem', color: '#64748b' }}>
                          {participantYearColumnSort === 'asc'
                            ? '▲'
                            : participantYearColumnSort === 'desc'
                              ? '▼'
                              : '⇅'}
                        </span>
                      </th>
                    );
                  }
                  if (col === '상태') {
                    return (
                      <th
                        key={col}
                        scope="col"
                        role="columnheader"
                        aria-sort={
                          dataErrorColumnSort === 'asc'
                            ? 'ascending'
                            : dataErrorColumnSort === 'desc'
                              ? 'descending'
                              : 'none'
                        }
                        title="클릭: 정상·불일치 순서(오름차순 → 내림차순 → 기본)"
                        onClick={() => {
                          setParticipantYearColumnSort('none');
                          setDataErrorColumnSort((prev) => cycleColumnSort(prev));
                        }}
                        style={{
                          ...thStyle,
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        {col}
                        <span style={{ marginLeft: '4px', fontSize: '0.65rem', color: '#64748b' }}>
                          {dataErrorColumnSort === 'asc'
                            ? '▲'
                            : dataErrorColumnSort === 'desc'
                              ? '▼'
                              : '⇅'}
                        </span>
                      </th>
                    );
                  }
                  return (
                    <th key={col} style={thStyle}>
                      {col}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ ...tdStyle, textAlign: 'center' }}>
                    불러오는 중…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} style={{ ...tdStyle, textAlign: 'center' }}>
                    조건에 맞는 명단이 없습니다.
                  </td>
                </tr>
              ) : (
                displayRows.map((row, idx) => {
                  const bad = row.mismatchedColumns || [];
                  const isPriorResigned = isPriorResignedRow(row, year, month);
                  const dataErrLabel =
                    row.상태 ??
                    (bad.length > 0 ? '불일치' : '정상');
                  return (
                    <tr key={`${row.성명}-${row.주민번호}-${idx}`}>
                      {COLUMNS.map((col) => (
                        <td
                          key={col}
                          style={{
                            ...tdStyle,
                            textAlign: CENTER_VALUE_COLUMNS.has(col) ? 'center' : tdStyle.textAlign,
                            color:
                              col === '상태' && dataErrLabel === '불일치'
                                ? '#b91c1c'
                                : bad.includes(col)
                                  ? '#b91c1c'
                                  : isPriorResigned && PRIOR_RESIGNED_HIGHLIGHT_COLUMNS.has(col)
                                    ? '#b91c1c'
                                  : undefined,
                            fontWeight:
                              col === '상태' && dataErrLabel === '불일치'
                                ? 600
                                : bad.includes(col)
                                  ? 600
                                  : isPriorResigned && PRIOR_RESIGNED_HIGHLIGHT_COLUMNS.has(col)
                                    ? 600
                                  : undefined,
                          }}
                        >
                          {col === '순번'
                            ? String(idx + 1)
                            : col === '참여연도'
                              ? row[col] != null && row[col] !== ''
                                ? String(row[col])
                                : ''
                              : col === '계좌번호'
                                ? row[col] != null && row[col] !== ''
                                  ? String(row[col])
                                  : ''
                              : col === '주민번호'
                                ? row[col] != null && row[col] !== ''
                                  ? String(row[col])
                                  : ''
                              : col === '상태'
                                ? dataErrLabel
                                : formatCell(row[col])}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SocialInsuranceExportPage;
